import { Button, ButtonMode, Tooltip } from '@creditsights/styleguide-web';
import React, { MouseEventHandler, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './TruncatedCompanyList.module.scss';

const cx = classNames.bind(styles);

export interface TruncatedCompanyListItem {
  title: string;
  url: string;
}

export interface TruncatedCompanyListProps {
  items: TruncatedCompanyListItem[];
  onClick?: MouseEventHandler<HTMLElement>;
  onMouseUp?: MouseEventHandler<HTMLElement>;
  isBold?: boolean;
  isDisabled?: boolean;
  isHovered?: boolean;
  className?: string;
}

export const TOOLTIP_ITEM_COUNT = 25;

const TruncatedCompanyList = ({
  items,
  onClick,
  onMouseUp,
  isBold,
  isDisabled,
  isHovered,
  className,
}: TruncatedCompanyListProps): ReactElement => {
  const { title, url } = items[0];
  const additionalLinks = items.slice(1, items.length);

  const renderAdditionalLinks = (onClick?: MouseEventHandler<HTMLElement>) => (
    <Tooltip
      content={
        <div className="content">
          {additionalLinks.slice(0, TOOLTIP_ITEM_COUNT).map(({ title }) => (
            <span key={title} data-testid="tooltip-item">
              {title}
            </span>
          ))}
          {additionalLinks.length > TOOLTIP_ITEM_COUNT && <span data-testid="tooltip-item">...</span>}
        </div>
      }
    >
      <Button mode={ButtonMode.PLAIN} onClick={onClick}>
        +{additionalLinks.length}
      </Button>
    </Tooltip>
  );

  const companyClass = cx('body', {
    'gray-out': isDisabled && isHovered,
    more: items.length > 1,
  });

  return (
    <div className={companyClass}>
      <Tooltip content={title}>
        <Link
          className={cx('link', 'main', className, { 'is-bolded': isBold })}
          key={title}
          data-testid="truncated-link-field"
          to={url}
          onMouseUp={onMouseUp}
        >
          <span data-testid="truncated-link-field-item">{title}</span>
        </Link>
      </Tooltip>
      {!!additionalLinks.length && (
        <div
          className={cx('link', className, { 'is-bolded': isBold, 'is-disabled': isDisabled })}
          data-testid="truncated-link-list-count"
          onMouseUp={onMouseUp}
        >
          {renderAdditionalLinks(onClick)}
        </div>
      )}
    </div>
  );
};

export default TruncatedCompanyList;
